import { simpleGqlClient } from '../graphql/simple-client.jsx';
import { buildClientePayload, mapAfipResponsabilidadIva } from './clientFiscal.js';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:4000';

export async function searchClients(filters = {}, options = {}) {
  const query = `
    query SearchClientes($page: Int, $pageSize: Int, $filter: ClienteFilter) {
      clientes(page: $page, pageSize: $pageSize, filter: $filter) {
        totalCount
        edges {
          node {
            id
            razonSocial
            cuit
            ${filters.documentoExacto !== undefined ? 'tipoDocumento' : ''}
            condicionIVA
            direccion
            pisoDepto
            codigoPostal
            telefono
            celular
            emails
            contacto
            provinciaId
            localidadId
            provincia { id nombre }
            localidad { id nombre }
            condicionPago
            plazoPago
            diasVencimiento
          }
        }
      }
    }
  `;

  const filter = {};
  if (filters.search) filter.search = filters.search;
  if (filters.provincia) filter.provincia = filters.provincia;
  if (filters.localidad) filter.localidad = filters.localidad;
  if (filters.tipoDocumento !== undefined) filter.tipoDocumento = filters.tipoDocumento;
  if (filters.documentoExacto !== undefined) filter.documentoExacto = filters.documentoExacto;

  const result = await simpleGqlClient.query(
    query,
    {
      page: filters.page || 1,
      pageSize: filters.pageSize || 10,
      filter,
    },
    options
  );

  if (result.errors) {
    throw Object.assign(new Error(result.errors[0]?.message || 'Error al buscar clientes'), { code: result.errors[0]?.extensions?.code });
  }

  const data = result.data?.clientes;
  if (filters.documentoExacto !== undefined && (!data || !Array.isArray(data.edges) || !Number.isInteger(data.totalCount))) {
    throw Object.assign(new Error('Búsqueda no disponible. Continúe manualmente.'), { code: 'DOCUMENT_LOOKUP_UNAVAILABLE' });
  }
  return {
    total: data?.totalCount || 0,
    data: (data?.edges || []).map(edge => edge.node),
  };
}

/** Sólo tipo y número revisados; null significa ausencia confirmada. */
export async function findClienteByDocument({ tipoDocumento, numeroDocumento }, options = {}) {
  const type = String(tipoDocumento || '').trim().toUpperCase();
  const source = String(numeroDocumento || '').trim();
  const number = source.replace(/[.\- ]/g, '');
  if (!/^[\d.\- ]+$/.test(source) || !((type === 'DNI' && /^\d{7,8}$/.test(number)) || (type === 'CUIT' && /^\d{11}$/.test(number)))) {
    throw Object.assign(new Error('Tipo o número de documento inválido.'), { code: 'BAD_USER_INPUT' });
  }
  const result = await searchClients({ tipoDocumento: type, documentoExacto: number, pageSize: 2 }, options);
  if (result.total > 1 || result.data.length > 1) {
    throw Object.assign(new Error('Hay varios clientes con ese documento. Continúe manualmente.'), { code: 'DOCUMENT_AMBIGUOUS' });
  }
  if (result.total !== result.data.length || result.data.some(client => !client?.id)) {
    throw Object.assign(new Error('Búsqueda no disponible. Continúe manualmente.'), { code: 'DOCUMENT_LOOKUP_UNAVAILABLE' });
  }
  return result.data[0] || null;
}

export async function getClienteById(id, options = {}) {
  const query = `
    query ClienteById($id: ID!) {
      cliente(id: $id) {
        id
        razonSocial
        cuit
        condicionIVA
        direccion
        pisoDepto
        codigoPostal
        telefono
        celular
        emails
        contacto
        provinciaId
        localidadId
        provincia { id nombre }
        localidad { id nombre }
        condicionPago
        plazoPago
        diasVencimiento
      }
    }
  `;

  const result = await simpleGqlClient.query(query, { id }, options);
  if (result.errors) {
    throw new Error(result.errors[0]?.message || 'Error al obtener el cliente');
  }

  const cliente = result.data?.cliente;
  if (!cliente) return null;

  try {
    const subcuentasResult = await simpleGqlClient.query(
      `
        query ClienteSubcuentasActivas($clienteId: ID!) {
          subcuentasCliente(clienteId: $clienteId, incluirInactivas: false) {
            id
            codigo
            nombre
            activo
          }
        }
      `,
      { clienteId: id },
      options,
    );

    return {
      ...cliente,
      subcuentas: subcuentasResult.errors
        ? []
        : Array.isArray(subcuentasResult.data?.subcuentasCliente)
          ? subcuentasResult.data.subcuentasCliente
          : [],
    };
  } catch {
    return { ...cliente, subcuentas: [] };
  }
}

export async function validateCUIT(cuit) {
  console.log('[STUB] validateCUIT', cuit);
  const normalized = String(cuit || '').replace(/\D/g, '');
  const isValid = normalized.length >= 8;
  return { ok: isValid };
}

export async function searchDuplicates(filters) {
  console.log('[STUB] searchDuplicates', filters);
  const hit = value => value && String(value).toUpperCase().startsWith('AC');
  const emails = Array.isArray(filters?.emails) ? filters.emails : [];
  const hasHit = hit(filters?.razon) || hit(filters?.cuit) || hit(emails[0]);
  return hasHit
    ? [
        { id: 'cl-1001', razon: 'ACME SRL', cuit: '30-12345678-9', email: 'ventas@acme.com' },
        { id: 'cl-1010', razon: 'ACERO SA', cuit: '30-23456789-0', email: 'info@acero.com' },
      ]
    : [];
}

/**
 * Obtiene todas las provincias desde GraphQL
 * @param {string} country - Código del país (default: 'AR')
 * @returns {Promise<Array>} Lista de provincias con id, nombre, codigo
 */
export async function getProvinces(country = 'AR', options = {}) {
  const query = `
    query GetProvincias {
      provincias {
        id
        nombre
        codigo
        activo
      }
    }
  `;

  try {
    const result = await simpleGqlClient.query(query, {}, options);
    
    if (result.errors) {
      console.error('GraphQL errors:', result.errors);
      throw new Error(result.errors[0]?.message || 'Error al cargar provincias');
    }

    if (!result.data || !result.data.provincias) {
      throw new Error('No se recibieron datos de provincias');
    }

    return result.data.provincias.map(p => ({
      id: p.id,
      nombre: p.nombre,
      codigo: p.codigo,
      name: p.nombre // Alias para compatibilidad
    }));
  } catch (error) {
    console.error('Error en getProvinces:', error);
    throw error;
  }
}

/**
 * Obtiene las localidades de una provincia desde GraphQL
 * @param {string} provinceId - ID de la provincia
 * @returns {Promise<Array>} Lista de localidades con id, nombre, codigoLocalidad
 */
export async function getLocalities(provinceId, options = {}) {
  if (!provinceId) {
    return [];
  }

  const query = `
    query GetLocalidades($provinciaId: ID!) {
      localidadesByProvincia(provinciaId: $provinciaId) {
        id
        nombre
        codigoLocalidad
        activo
      }
    }
  `;

  try {
    const result = await simpleGqlClient.query(query, { provinciaId: provinceId }, options);
    
    if (result.errors) {
      console.error('GraphQL errors:', result.errors);
      throw new Error(result.errors[0]?.message || 'Error al cargar localidades');
    }

    if (!result.data || !result.data.localidadesByProvincia) {
      throw new Error('No se recibieron datos de localidades');
    }

    return result.data.localidadesByProvincia.map(loc => ({
      id: loc.id,
      nombre: loc.nombre,
      codigo: loc.codigoLocalidad,
      codigoLocalidad: loc.codigoLocalidad,
      name: loc.nombre // Alias para compatibilidad
    }));
  } catch (error) {
    console.error('Error en getLocalities:', error);
    throw error;
  }
}

export async function createCliente(payload) {
  // El alta documental no registra datos personales ni reintenta una escritura incierta.
  const documentFlow = payload?.validarDocumentoExacto === true;
  // Intentar crear cliente vía GraphQL (simpleGqlClient)
  const mutation = `
    mutation CreateCliente($input: ClienteInput!) {
      createCliente(input: $input) {
        id
        razonSocial
        activo
      }
    }
  `;

  try {
    // Normalizar y limpiar el payload para ajustarlo al schema GraphQL del API
    const prepareInput = (p) => {
      const normalizedPayload = buildClientePayload(p);
      const allowed = [
        // Información General
        'razonSocial', 'nombreFantasia', 'tipoDocumento', 'cuit', 'condicionIVA', 'categoriaCliente',
        // Dirección
        'direccion', 'pisoDepto', 'pais', 'provinciaId', 'localidadId', 'codigoLocalidad', 'codigoPostal', 'observaciones',
        // Contacto
        'contacto', 'rolContacto', 'telefono', 'celular', 'telefonoAlternativo', 'emails', 'sitioWeb', 'horarioAtencion', 'notasContacto', 'isPrincipal',
        // Comercial
        'empresa', 'sucursal', 'vendedor', 'tipoCliente', 'listaPrecio', 'condicionVenta', 'plazoPago', 'metodoPago', 'limiteCredito', 'zonaReparto', 'observacionesComerciales',
        // Metadata
        'activo', 'fechaAlta', 'validarDocumentoExacto'
      ];

      const input = {};
      for (const k of allowed) {
        if (Object.prototype.hasOwnProperty.call(normalizedPayload, k)) {
          let v = normalizedPayload[k];

          // Normalizaciones especiales
          if (v === '' || v === undefined) {
            // Omitir cadenas vacías para evitar errores de enum/Int/Float
            continue;
          }

          if (k === 'plazoPago') {
            const n = parseInt(v, 10);
            if (!Number.isNaN(n)) input[k] = n;
            continue;
          }

          if (k === 'limiteCredito') {
            const f = parseFloat(String(v).replace(',', '.'));
            if (!Number.isNaN(f)) input[k] = f;
            continue;
          }

          if (k === 'emails') {
            if (Array.isArray(v)) {
              input[k] = v.filter(Boolean);
            } else if (typeof v === 'string') {
              input[k] = v.split(',').map(s => s.trim()).filter(Boolean);
            }
            continue;
          }

          // For IDs, allow strings but empty -> omit
          if ((k === 'provinciaId' || k === 'localidadId') && (v === null || v === '')) {
            continue;
          }

          input[k] = v;
        }
      }

      return input;
    };

    const input = prepareInput(payload);

    // Validación temprana de campos obligatorios según ClienteInput
    const requiredFields = ['razonSocial', 'tipoDocumento', 'cuit', 'condicionIVA', 'categoriaCliente'];
    const missing = requiredFields.filter(f => {
      const v = input[f];
      return v === undefined || v === null || (typeof v === 'string' && v.trim() === '');
    });

    if (missing.length) {
      console.warn('[createCliente] Faltan campos obligatorios antes de enviar GraphQL:', missing);
      return { success: false, message: `Faltan campos obligatorios: ${missing.join(', ')}`, missing };
    }

    // El flujo documental nunca registra payloads.
    if (!documentFlow) try {
      console.groupCollapsed('[createCliente] GraphQL Request');
      console.log('Mutation:', mutation);
      console.log('Variables (input):', input);
      console.groupEnd();
    } catch (err) {
      // No bloquear si el console.* falla en algún entorno
      console.warn('[createCliente] Error al loggear la request', err);
    }

    const result = await simpleGqlClient.query(mutation, { input });

    // No registrar tampoco respuestas del flujo documental.
    if (!documentFlow) try {
      console.groupCollapsed('[createCliente] GraphQL Response');
      // Loguear JSON completo para facilitar inspección de `errors[].extensions` y stack
      try {
        console.log('Full result JSON:', JSON.stringify(result, null, 2));
      } catch (jerr) {
        // Si stringify falla (objetos con ciclos), fallback al object normal
        console.log('Full result (object):', result);
      }

      if (result && result.data && result.data.createCliente) {
        console.log('createCliente:', result.data.createCliente);
      }
      if (result && result.errors) {
        console.error('GraphQL errors creating cliente:', result.errors);

        // Log específico y legible para debugging: mensaje y extensions
        try {
          const e0 = result.errors[0];
          if (e0) {
            console.error('errors[0].message:', e0.message);
            try {
              console.error('errors[0].extensions:', JSON.stringify(e0.extensions, null, 2));
            } catch (se) {
              console.error('errors[0].extensions (object):', e0.extensions);
            }

            // Algunos servers (Apollo) ponen stacktrace en extensions.exception.stacktrace
            if (e0.extensions && e0.extensions.exception && e0.extensions.exception.stacktrace) {
              try {
                console.error('Server stacktrace:\n', e0.extensions.exception.stacktrace.join('\n'));
              } catch (stErr) {
                console.error('Server stacktrace (raw):', e0.extensions.exception.stacktrace);
              }
            }
          }
        } catch (logErr) {
          console.warn('[createCliente] Error al loggear errors[0]', logErr);
        }
      }
      console.groupEnd();
    } catch (err) {
      console.warn('[createCliente] Error al loggear la response', err);
    }

    if (result.errors) {
      // Si el servidor devuelve INTERNAL_ERROR, intentar un reintento con payload mínimo
      try {
        const e0 = result.errors[0];
        if (!documentFlow && e0 && e0.extensions && e0.extensions.code === 'INTERNAL_ERROR') {
          try {
            const minimal = {
              // Campos modernos mínimos compatibles con ClienteInput
              razonSocial: input.razonSocial,
              tipoDocumento: input.tipoDocumento,
              cuit: input.cuit,
              condicionIVA: input.condicionIVA,
              categoriaCliente: input.categoriaCliente,
              // Contacto mínimo (mantener como arreglo si existe)
              emails: Array.isArray(input.emails) && input.emails.length ? [input.emails[0]] : undefined,
              telefono: input.telefono || input.celular || undefined,
              direccion: input.direccion || undefined,
              // NOTA: no introducir campos antiguos como `nombre` o `documento`
              // que no forman parte de `ClienteInput` (evita BAD_USER_INPUT)
            };

            console.warn('[createCliente] Server returned INTERNAL_ERROR, retrying with minimal payload:', minimal);
            const retryResult = await simpleGqlClient.query(mutation, { input: minimal });

            try {
              console.groupCollapsed('[createCliente] Retry GraphQL Response');
              try { console.log('Full retry result JSON:', JSON.stringify(retryResult, null, 2)); } catch (je) { console.log('Retry result:', retryResult); }
              if (retryResult && retryResult.errors) console.error('Retry errors:', retryResult.errors);
              console.groupEnd();
            } catch (lg) {
              console.warn('[createCliente] Error logging retry result', lg);
            }

            if (!retryResult.errors && retryResult.data && retryResult.data.createCliente) {
              const createdRetry = retryResult.data.createCliente;
              console.info('[createCliente] Retry successful, created id:', createdRetry.id);
              return { success: true, id: createdRetry.id, data: createdRetry };
            }
          } catch (retryErr) {
            console.warn('[createCliente] Retry with minimal payload failed', retryErr);
            // continuar para devolver el error original
          }
        }
      } catch (ie) {
        console.warn('[createCliente] Error handling INTERNAL_ERROR retry logic', ie);
      }

      return { success: false, message: result.errors[0]?.message || 'Error al crear cliente', code: result.errors[0]?.extensions?.code, raw: result };
    }

    if (!result.data || !result.data.createCliente) {
      if (!documentFlow) console.warn('createCliente: no data returned', result);
      return { success: false, message: 'No se recibió respuesta del servidor', raw: result };
    }

    const created = result.data.createCliente;
    return { success: true, id: created.id, data: created };
  } catch (error) {
    if (!documentFlow) console.error('Error en createCliente:', error);
    return { success: false, message: error.message || 'Error interno', code: error.code || error.extensions?.code };
  }
}

/**
 * Crea un cliente completo con contactos, domicilios y configuraciones
 * @param {Object} payload - Payload completo del cliente
 * @returns {Promise<Object>} Resultado de la operación
 */
export async function createClienteCompleto(payload) {
  const mutation = `
    mutation CreateClienteCompleto($input: ClienteCompletoInput!) {
      createClienteCompleto(input: $input) {
        id
        razonSocial
        activo
      }
    }
  `;

  const prepareClienteInput = (cliente = {}) => {
    const normalizedCliente = buildClientePayload(cliente);
    const allowed = [
      'razonSocial',
      'nombreFantasia',
      'tipoDocumento',
      'cuit',
      'condicionIVA',
      'categoriaCliente',
      'codigo',
      'tipoCliente',
      'estado',
      'motivoEstado',
      'cp',
      'lat',
      'lng',
      'direccion',
      'pisoDepto',
      'pais',
      'provinciaId',
      'localidadId',
      'codigoLocalidad',
      'codigoPostal',
      'observaciones',
      'contacto',
      'rolContacto',
      'telefono',
      'celular',
      'telefonoAlternativo',
      'emails',
      'sitioWeb',
      'horarioAtencion',
      'notasContacto',
      'isPrincipal',
      'empresa',
      'sucursal',
      'vendedor',
      'listaPrecio',
      'condicionVenta',
      'condicionPago',
      'plazoPago',
      'metodoPago',
      'limiteCredito',
      'diasVencimiento',
      'toleranciaVencidos',
      'prioridadOperativa',
      'bloqueoPorDeuda',
      'umbralDeuda',
      'slaDespachoHs',
      'slaEntregaHs',
      'zonaReparto',
      'observacionesComerciales',
      'activo',
      'fechaAlta',
    ];

    const input = {};
    for (const key of allowed) {
      if (!Object.prototype.hasOwnProperty.call(normalizedCliente, key)) continue;
      let value = normalizedCliente[key];

      if (value === '' || value === undefined) continue;

      if (['plazoPago', 'diasVencimiento', 'toleranciaVencidos', 'slaDespachoHs', 'slaEntregaHs'].includes(key)) {
        const n = parseInt(value, 10);
        if (!Number.isNaN(n)) input[key] = n;
        continue;
      }

      if (['limiteCredito', 'umbralDeuda', 'lat', 'lng'].includes(key)) {
        const f = parseFloat(String(value).replace(',', '.'));
        if (!Number.isNaN(f)) input[key] = f;
        continue;
      }

      if (key === 'emails') {
        if (Array.isArray(value)) {
          input[key] = value.filter(Boolean);
        } else if (typeof value === 'string') {
          input[key] = value.split(',').map(s => s.trim()).filter(Boolean);
        }
        continue;
      }

      input[key] = value;
    }
    return input;
  };

  const buildNotificacionesConfig = (config) => {
    if (!config) return null;
    return {
      destinosGenerales: JSON.stringify(config.destinosGenerales || {}),
      eventosConfig: JSON.stringify(config.avisosPorEstado || {}),
      noEnviarFueraHorario: Boolean(config.noEnviarFueraHorario),
      horarioDesde: config.horarioDesde || null,
      horarioHasta: config.horarioHasta || null,
      activo: config.activo !== false,
    };
  };

  const buildTarifasConfig = (config) => {
    if (!config) return null;
    return {
      tarifaBaseId: config.tarifaBaseId || null,
      descuentoPct:
        config.descuentoPct !== undefined && config.descuentoPct !== ''
          ? parseFloat(config.descuentoPct)
          : null,
      recargoPct:
        config.recargoPct !== undefined && config.recargoPct !== ''
          ? parseFloat(config.recargoPct)
          : null,
      requiereConfirmacion: Boolean(config.requiereConfirmacion),
      prioridadDespacho: Boolean(config.prioridadDespacho),
      prioridadEntrega: Boolean(config.prioridadEntrega),
      observaciones: config.observaciones || null,
      activo: config.activo !== false,
    };
  };

  const input = {
    cliente: prepareClienteInput(payload?.cliente),
    contactos: Array.isArray(payload?.contactos) ? payload.contactos : [],
    domicilios: Array.isArray(payload?.domicilios) ? payload.domicilios : [],
    notificacionesConfig: buildNotificacionesConfig(payload?.notificacionesConfig),
    tarifasConfig: buildTarifasConfig(payload?.tarifasConfig),
  };

  try {
    const result = await simpleGqlClient.query(mutation, { input });
    if (result.errors) {
      return { success: false, message: result.errors[0]?.message || 'Error al crear cliente', raw: result };
    }
    return { success: true, data: result.data?.createClienteCompleto, id: result.data?.createClienteCompleto?.id };
  } catch (error) {
    console.error('Error en createClienteCompleto:', error);
    return { success: false, message: error.message || 'Error interno' };
  }
}

/**
 * Consulta el Padrón AFIP A13 para autocompletar datos del cliente
 * @param {string} cuitOrDni - CUIT (11 dígitos) o DNI (7-8 dígitos)
 * @param {boolean} withFCE - Si debe incluir verificación de empresa grande FCEM
 * @returns {Promise<Object>} Datos del contribuyente desde AFIP
 */
export async function consultarPadronAFIP(cuitOrDni, withFCE = false) {
  const normalized = String(cuitOrDni || '').replace(/\D/g, '');
  
  if (!normalized || (normalized.length !== 11 && normalized.length < 7)) {
    throw new Error('Ingrese un CUIT válido (11 dígitos) o DNI (7-8 dígitos)');
  }

  try {
    const url = `${API_BASE}/padron/consultar?cuit=${normalized}${withFCE ? '&withFCE=1' : ''}`;
    console.log('🔍 Consultando AFIP:', url);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
    });

    console.log('📡 Response status:', response.status, response.statusText);

    // Si la respuesta no es OK, intentar leer el contenido
    if (!response.ok) {
      const contentType = response.headers.get('content-type');
      console.log('📄 Content-Type:', contentType);
      
      // Si es HTML, probablemente es un error 404 o 500 del servidor
      if (contentType && contentType.includes('text/html')) {
        const htmlText = await response.text();
        console.error('❌ Recibido HTML en lugar de JSON:', htmlText.substring(0, 500));
        throw new Error(`El servidor devolvió HTML (Error ${response.status}). Verifica que la ruta del API sea correcta: ${url}`);
      }
      
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.msg || `Error HTTP ${response.status}`);
    }

    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      const text = await response.text();
      console.error('❌ Respuesta no es JSON:', text.substring(0, 500));
      throw new Error('El servidor no devolvió JSON válido. Verifica la configuración del endpoint.');
    }

    const data = await response.json();
    console.log('✅ Datos recibidos de AFIP:', data);

    if (!data.ok) {
      // Devolver el objeto de error tal cual para que el caller pueda manejarlo
      // de forma más amigable (mostrar toast, no imprimir stack trace).
      return { ok: false, msg: data.msg || 'Error al consultar AFIP', _raw: data };
    }

    // Preserve the raw persona payload when available so callers
    // (ej. GeneralTab.jsx) can inspect persona_raw.domicilio[] y mapear direcciones.
    const persona_raw = data.persona_raw || data.persona || (data.personaReturn && data.personaReturn.persona) || null;
    const raw_resp = data.raw_resp || data.raw || data;

    return {
      ok: true,
      cuit: data.cuit,
      tipoDocumento: data.tipoDocumento || 'CUIT',
      tipoPersona: data.tipoPersona,
      razonSocial: data.razonSocial,
      nombreFantasia: data.nombreFantasia || '',
      condicionIVA: mapAfipResponsabilidadIva(data.condicionIVA, { format: 'label' }),
      estadoContribuyente: data.estadoContribuyente,
      actividadPrincipal: data.actividadPrincipal,
      // Campos simplificados (compatibilidad)
      direccion: data.direccion,
      provincia: data.provincia,
      localidad: data.localidad,
      codigoPostal: data.codigoPostal,
      pais: data.pais || 'Argentina',
      // Payloads completos para uso avanzado
      persona_raw,
      raw_resp,
      esGrandeEmpresa: data.esGrandeEmpresa,
    };
  } catch (error) {
    console.error('❌ Error consultando Padrón AFIP:', error);
    throw error;
  }
}

export { API_BASE };
